import _ from 'underscore';
import * as fastLevenshtein from 'fast-levenshtein';
import { defaultData } from './data';

const DEFAULT_MAX_QUESTION_COUNT = 3;
const DEFAULT_RESULT_KEY = 'default';
const TROUBLESHOOTING_KEY = 'Troubleshooting';
const defaultI18n = {
  getString: value => value,
};

/**
 * Search the dataset with the shortest distance to given query.
 * @param {string} query - The search query.
 * @param {string} data - The dataset t search within.
 * @param {number} length - The number of questions to return.
 * @returns {string[]} - An array containing the top length questions matching the search query.
 */
function getNMatchedQuestion(query, data, length) {
  return data
    .map((question) => {
      const { text } = question;
      return { question, distance: fastLevenshtein.get(query, text) };
    })
    .sort((a, b) => a.distance - b.distance)
    .slice(0, length)
    .map(item => item.question);
}

/**
 * Search the dataset for questions matching the given query.
 * @param {string} query - The search query.
 * @param {object} [i18n=defaultI18n] - i18n object.
 * @param {boolean} [isPageSearch=false] - Indicates whether the search is restricted to a specific page.
 * @param {Object} [options] - Optional parameters.
 * @param {Number} [options.maxQuestionCount=3] - Max number of questions to return.
 * @param {object} [options.data=defaultData] - The dataset to search within.
 * @param {string} [options.defaultResultKey='default'] - The key to use when searching for default results.
 * @param {boolean} [options.isTroubleshootingEnabled=false] - Indicates whether troubleshooting is enabled.
 * @returns {IQuestion[]} - An array containing the top 3 questions matching the search query.
 */
export const searchQuestions = (
  query,
  i18n = defaultI18n,
  isPageSearch = false,
  {
    maxQuestionCount = DEFAULT_MAX_QUESTION_COUNT,
    data = defaultData,
    defaultResultKey = DEFAULT_RESULT_KEY,
    isTroubleshootingEnabled = false,
  } = {}
) => {
  if (query === '') return [];

  if (isPageSearch) {
    let pageResult;
    if (query in data) {
      pageResult = data[query]; // Use page-specific result
    } else if (defaultResultKey in data) {
      pageResult = data[defaultResultKey]; // Use the default result
    } else {
      pageResult = []; // Return an empty array if both page and default result don't exist
    }

    // general question list
    const questionList = pageResult
      .slice(0, maxQuestionCount)
      .map(question => ({
        ...question,
        text: question.text || i18n.getString(question.translationKey),
      }));

    if (isTroubleshootingEnabled) {
      const troubleshootingQuestion = _.first(data[TROUBLESHOOTING_KEY]);

      // insert troubleshooting question as the third question
      questionList.splice(2, 1, {
        ...troubleshootingQuestion,
        text: troubleshootingQuestion.text || i18n.getString(troubleshootingQuestion.translationKey),
      });
    }

    return questionList;
  }

  // get a flat array of all non-default questions
  const nonDefaultValues = Object.entries(data)
    .filter(([key]) => key !== defaultResultKey && key !== TROUBLESHOOTING_KEY)
    .flatMap(([, value]) => value);

  const nonDefaultQuestions = _.uniq(nonDefaultValues)
    .map(question => ({
      ...question,
      text: question.text || i18n.getString(question.translationKey),
    }));
  const allQuestions = [...nonDefaultQuestions];

  // return the top 3 questions with the closest string match to the query
  const matchedQuestions = allQuestions
    .filter(question => question.text.toLowerCase().includes(query.toLowerCase()));

  const results = getNMatchedQuestion(query, matchedQuestions, maxQuestionCount);
  return results;
};

export function isTroubleshootingQuery({
  query,
  i18n,
}) {
  const troubleshootingList = defaultData[TROUBLESHOOTING_KEY];
  return _.any(troubleshootingList, q => query === i18n.getString(q.translationKey));
}
